<template>
  <div :class="['kpi-block', wrapperClasses]">
    <div class="kpi-title"> {{ title }} </div>
    <div class="data-block">
      <svg v-if="iconId" :class="iconId" :viewBox="`0 0 ${iconViewbox}`">
        <use :xlink:href="`#${iconId}`" width="22px" height="22px"></use>
      </svg>
      <loading-icon v-if="loading"></loading-icon>
      <span v-else
            :class="['kpi-data', {['no-data']: formattedKpiData === '--' }]">
        {{formattedKpiData}}
      </span>
    </div>
  </div>
</template>

<script>
  import { brandsAPI } from '../../services/api/brands'
  import { NumberFormatters } from '../mixins/NumberFormatters.js'
  import LoadingIcon from '../Shared/LoadingIcon'

  export default {
    props: {
      apiParams: Object,
      calculatedData: [Number, String],
      currency: Boolean,
      iconId: String,
      iconViewbox: String,
      kpiKey: String,
      percentage: Boolean,
      shouldSendBack: Boolean,
      title: String,
      url: String,
      usesLift: Boolean,
      wrapperClasses: String,
      retailerIds: Array
    },

    name: 'KpiBlock',

    components: {
      LoadingIcon,
    },

    mixins: [NumberFormatters],

    data() {
      return {
        errorMessage: null,
        delayLoad: null,
        kpiValue: null,
        liftData: null,
        loading: true,
        previousKpiValue: null,
        showLift: false,
      }
    },

    methods: {
      initKpi() {
        if (this.url) {
          this.getKpiData()
        } else {
          this.setCalculatedData()
        }
      },

      async getKpiData() {
        if (this.url && this.apiParams?.params) {
          this.loading = true
          try {
            const data = await brandsAPI.getKpi(this.url, this.apiParams.params)
            this.setKpi(data, null)
          } catch(error) {
            this.setKpi(null, error)
          }
        }
      },

      sendBackKpiValue(dataToSendBack) {
        if (this.shouldSendBack) {
          const value = dataToSendBack ? dataToSendBack : 0
          this.$emit('setKpiValue', this.kpiKey, value)
        }
      },

      setKpi(data, error) {
        let errorText = `unable to load ${this.title} data`,
          dataToSendBack
        if (data) {
          const kpiValue = data[this.kpiKey]
          const kpiData = this.kpiKey === 'total_units' && Array.isArray(kpiValue) ? this.sumTotalUnits(kpiValue) : kpiValue,
            prevKpiData = data[`previous_${this.kpiKey}`]
          dataToSendBack = kpiData
          this.kpiValue = this.abbrNum(kpiData, 2)
          this.previousKpiValue = this.usesLift ? prevKpiData : null
          this.errorMessage = null
        } else {
          dataToSendBack = null
          this.kpiValue = null
          this.errorMessage = error ? `${errorText}: ${error}` : errorText
        }
        this.loading = false
        this.sendBackKpiValue(dataToSendBack)
        this.configureLiftData(dataToSendBack)
      },

      sumTotalUnits(totalUnitsData) {
        return totalUnitsData.reduce((a, b) => a + (b['total_units'] || 0), 0);
      },

      configureLiftData(kpiData) {
        if (this.usesLift) {
          if (this.previousKpiValue > 0) {
            let percentChange = ((kpiData - this.previousKpiValue)/this.previousKpiValue) * 100
            this.liftData = Math.round(percentChange)
          } else {
            this.liftData = null
          }
          this.$emit('setLiftData', this.liftData, this.kpiKey)
        }
      },

      setCalculatedData() {
        if (this.calculatedData || typeof this.calculatedData === 'number') {
          this.kpiValue = this.calculatedData
          this.loading = false
        }
      }
    },

    created() {
      this.initKpi()
    },

    computed: {
      formattedKpiData: function() {
        const data = this.kpiValue,
          formattedKpi = this.currency ? `$${data}` : this.percentage ? `${data}%` : data
        return data || typeof data === 'number' ? formattedKpi : '--'
      }
    },

    watch: {
      apiParams: function() {
        this.getKpiData()
      },

      calculatedData: function() {
        this.setCalculatedData()
      },
    },
  }
</script>

<style lang="sass">
@import '../../assets/sass/colors'

.kpi-block
  color: $title-color
  display: flex
  height: 100%
  flex-flow: column
  justify-content: flex-end

  .error
    color: $negative-hover
    font-size: 0.8rem

  .no-data
    color: $title-color

svg.loading-icon-svg
  margin-right: 0
</style>